<template>
  <Card
    :title="$t('emailinitialization.general.title')"
    :footerDivider="false"
    :loadingState="loadingInitialEmail"
  >
    <template v-slot:infobox>
      <h3>{{ $t('emailinitialization.infoBox.title') }}</h3>
      {{ initialEmail }}
    </template>
    <template v-slot:content>
      <div id="newEmail">
        <BasicTextfield
          @input="onNewEmailInputIsUpdated"
          id="email-change.neue-email-input"
          type="email"
          formnovalidate="true"
          @update:input="onEmailInput"
          @error="toggleNewEmailError"
          @keyup.enter="onSubmit"
          :value="newEmail"
          :status="newEmailTextfieldStatus"
          :label="$t('emailinitialization.newEmail.label')"
          :placeholder="$t('emailinitialization.newEmail.placeholder')"
          :required="true"
        >
          <template v-slot:info>{{ $t('emailinitialization.newEmail.info') }}</template>
          <template v-slot:error>{{ newEmailErrorMessage }}</template>
        </BasicTextfield>
      </div>
    </template>
    <template v-slot:footer>
      <div class="footer-cta footer-cta--left">
        <CustomButton
          @click="onSubmit"
          buttonId="email-change.neue-email-weiter-button"
          :pendingState="loading"
          :buttonColor="ButtonColor.PRIMARY"
        >
          {{ $t('emailinitialization.submit.text') }}
        </CustomButton>
        <CustomButton
          @click="onCancel"
          buttonId="email-change.neue-email-abbrechen-button"
          :buttonColor="ButtonColor.LINK"
        >
          {{ $t('emailinitialization.cancel.text') }}
        </CustomButton>
      </div>
    </template>
  </Card>
</template>

<script setup lang="ts">
import {
  BasicTextfield,
  ButtonColor,
  Card,
  CustomButton,
  InputStatus
} from 'de-zurich-ui-library'
import { navigate } from '@/utils/NavigationUtils'
import { updateEmailAddress } from '@/stores/accountManagementService'
import { usePoliciesStore } from '@/stores/policiesStore'
import { navigateBackFromEmailChange } from '@/components/account-management/emailchange/EmailChangeNavigation'
import { ResponseError } from 'account-api'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { checkIfEmailInputIsValid } from '@/components/account-management/accountcreation/accountcreationUtils'

const { t } = useI18n()
const policiesStore = usePoliciesStore()

const props = defineProps({
  initialEmail: {
    type: String,
    required: true
  },
  accountId: {
    type: String,
    required: true
  },
  loadingInitialEmail: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits<{
  initiateEmailChange: [none: void]
}>()

const newEmail = ref('')
const newEmailTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)
const newEmailErrorMessage = ref('')
const loading = ref(false)

const onEmailInput = (): void => {
  newEmailTextfieldStatus.value = InputStatus.DEFAULT
}

const validateEmail = (): void => {
  if (!newEmail.value || newEmail.value === '') {
    newEmailTextfieldStatus.value = InputStatus.ERROR
    newEmailErrorMessage.value = t('emailinitialization.newEmail.errors.empty')
  } else if (InputStatus.ERROR === checkIfEmailInputIsValid(newEmail.value)) {
    newEmailTextfieldStatus.value = InputStatus.ERROR
    newEmailErrorMessage.value = t('emailinitialization.newEmail.errors.invalidEmail')
  }
}

const onNewEmailInputIsUpdated = (val: string): void => {
  newEmail.value = val
  validateEmail()
}

const toggleNewEmailError = (): void => {
  newEmailTextfieldStatus.value = InputStatus.ERROR
}

const onSubmit = async (): Promise<void> => {
  validateEmail()
  if (newEmailTextfieldStatus.value === InputStatus.ERROR) {
    return
  }
  loading.value = true
  try {
    await updateEmailAddress(props.accountId, newEmail.value)
    emit('initiateEmailChange')
  } catch (error) {
    if (error instanceof ResponseError) {
      if (error.response.status === 401) {
        navigate('/')
      } else {
        navigate(globalThis.ENV_CONFIG?.REDIRECTS.TECHNICAL_ERROR_PAGE)
      }
    } else {
      navigate(globalThis.ENV_CONFIG?.REDIRECTS.TECHNICAL_ERROR_PAGE)
    }
  } finally {
    loading.value = false
  }
}

const onCancel = (): void => {
  navigateBackFromEmailChange(policiesStore)
}
</script>
